import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DistrictSimpleDto } from 'src/app/common/dtos/district.dto';
import { LMSCourseService } from '../../../services/lms-courses/lms-course.service';
import {
  ItemLmsCourseAPIResponse,
  LmsCourseAPIResponse,
  LmsCourseStatus,
} from '../../../types/responses/lms-course.responses';
import { DistrictSelectComponent } from '../../district-select/district-select.component';
import { ModalComponent } from '../../modals/modal/modal.component';

@Component({
  selector: 'app-lms-course-edit-modal',
  standalone: false,
  templateUrl: './lms-course-edit-modal.component.html',
  styleUrl: './lms-course-edit-modal.component.scss',
})
export class LmsCourseEditModalComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @ViewChild('modal') modal: ModalComponent;

  @ViewChild('shareWithDistrictSelect')
  shareWithDistrictSelect: DistrictSelectComponent;

  @ViewChild('excludeFromDistrictSelect')
  excludeFromDistrictSelect: DistrictSelectComponent;

  @Input() courseId: number;

  @Output() readonly modalReset = new EventEmitter();

  course: LmsCourseAPIResponse | null;

  subs: Subscription[] = [];

  isLoading: boolean;

  districtsSharedWith: DistrictSimpleDto[];

  districtsExcludedFrom: DistrictSimpleDto[];

  LmsCourseStatus = LmsCourseStatus;

  constructor(private CourseService: LMSCourseService) {}

  ngOnInit() {
    if (this.courseId) {
      this.loadCourse();
    }
  }

  ngAfterViewInit(): void {
    this.modal.open();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['courseId']) {
      this.loadCourse();
    }
  }

  loadCourse() {
    this.isLoading = true;
    if (this.courseId) {
      this.subs.push(
        this.CourseService.getCourse(
          this.courseId,
          'registrationCount,districtsSharedWith.district,districtsExcludedFrom.district'
        ).subscribe((resp: ItemLmsCourseAPIResponse) => {
          this.course = resp.item;
          if (this.course.districtsSharedWith) {
            this.districtsSharedWith = this.course.districtsSharedWith.map(
              (district) => district.district
            );
          }
          if (this.course.districtsExcludedFrom) {
            this.districtsExcludedFrom = this.course.districtsExcludedFrom.map(
              (district) => district.district
            );
          }
          this.isLoading = false;
        })
      );
    }
  }

  updateStatus() {
    if (this.course) {
      this.CourseService.updateCourse(this.courseId, {
        status: this.course.status,
      });
    }
  }

  toggleSharing($event: Event) {
    if (this.course) {
      const inputElement = $event.target as HTMLInputElement;
      this.course.is_shared = inputElement.checked ? 1 : 0;
      this.CourseService.updateCourse(this.courseId, {
        is_shared: this.course.is_shared,
      });
    }
  }

  handleShareDistricts(incomingDistricts: DistrictSimpleDto[]) {
    if (this.course) {
      this.subs.push(
        this.CourseService.handleShareDistricts(
          this.course,
          incomingDistricts
        ).subscribe((response: number[]) => {
          const incomingDistrictIds = response;
          // Update the exclude dropdown's internalSelectedDistrictList
          this.excludeFromDistrictSelect.internalSelectedDistrictList =
            this.excludeFromDistrictSelect.internalSelectedDistrictList.filter(
              (district) => !incomingDistrictIds.includes(district.id)
            );
        })
      );
    }
  }

  handleExcludeDistricts(incomingDistricts: DistrictSimpleDto[]) {
    if (this.course) {
      this.subs.push(
        this.CourseService.handleExcludeDistricts(
          this.course,
          incomingDistricts
        ).subscribe((response: number[]) => {
          const incomingDistrictIds = response;
          // Update the share dropdown's internalSelectedDistrictList
          this.shareWithDistrictSelect.internalSelectedDistrictList =
            this.shareWithDistrictSelect.internalSelectedDistrictList.filter(
              (district) => !incomingDistrictIds.includes(district.id)
            );
        })
      );
    }
  }

  resetModal() {
    this.course = null;
    this.modalReset.emit();
  }

  deleteCourse() {
    this.CourseService.deleteCourse(this.courseId).subscribe(() => {
      this.modal.close();
    });
  }

  ngOnDestroy(): void {
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }

    if (this.modal) {
      this.modal.close();
    }
    this.modalReset.emit();
  }
}
